import { defineStore } from "pinia";
import { getEnhancedPhoneNumber } from "~/utils/helpers/inputFormatter";
import { useToastStore } from "@/stores/toast";
import { convertSec, getShamsiDate, convertDateToUnix, timeToSeconds, isoDate } from "~/utils/helpers/dateTimeFormatter";
import type { IContactCallState, } from "~/types/contact/Types";
import { useCdrStore } from "~/stores/cdr";
import { useContactStore } from "~/stores/contact"
import { useContactFormStore } from "~/stores/contact/form"
import { parseJwtPayload } from "~/types/permission/Types";
import { useFileManagerStore } from "~/stores/fileManager";

export const useContactCallStore = defineStore("ContactCallStore", {
  state: (): IContactCallState => ({
    calls: {
      all: [],
      outgoing: [],
      incoming: [],
      missed: [],
    },
    callsCount: {
      all: 0,
      outgoing: 0,
      incoming: 0,
      missed: 0,
    },
    lastPlayedId: null,
    lastPlayedAudio: null,
    callerInfo: {},
    callReason: {
      comment: "",
      followDate: "",
      reminderType: 0,
      reminderDate: ""
    }
  }),

  getters: {
    user_phone: () => {
      const contactStore = useContactStore()
      return contactStore.formData?.phone_number;
    },
  },

  actions: {
    async dial(phone: string) {
      const jwt = parseJwtPayload();
      try {
        const body = {
          to: getEnhancedPhoneNumber(phone),
          from: jwt.phone,
          admin: jwt.adphone ? jwt.adphone : jwt.phone,
        };
        const data = await $fetch<any>("/api/contact/call/dial", {
          method: "POST",
          body: body,
        });
        return data;
      } catch (err: any) {
        console.log(err);
      }
    },

    async existByPhoneNumber(params: any) {
      const contactFormStore = useContactFormStore();
      const fileManagerStore = useFileManagerStore();

      const formsUlids = contactFormStore.formsList.map(form => form.ulid);
      const data = {
        forms: formsUlids,
        contains: params.PhoneNumber,
        service_type: "ci",
        field_name: "phone_number"
      };
      const searchResult = await $fetch<any>(`/api/contact/data/search`, {
        method: "POST",
        body: data,
      });

      if (params.fullCdr) {
        console.log("existByPhoneNumber: ", data)
        return data
      }

      this.callerInfo = {
        phoneNumber: params.PhoneNumber,
        x_cid: params.x_cid,
        callid: params.callid,
      };

      let targetItem = {};
      if (searchResult.companies) {
        targetItem = searchResult.companies[0];
      } else if (searchResult.people) {
        targetItem = searchResult.people[0];
      }
      if (targetItem.form_ulid) {
        let avatar = ""
        if (targetItem.values.Avatar) {
          avatar = await fileManagerStore.downloadFile({ id: targetItem.values.Avatar }, false)
        }
        this.callerInfo = {
          ...this.callerInfo,
          form_ulid: targetItem.form_ulid,
          form_type: targetItem.form_type,
          name: targetItem.values.name,
          company_name: targetItem.values.company_name,
          phone_number: targetItem.values.phone_number,
          role: targetItem.values.role,
          contact_id: targetItem.ulid,
          followUp_operator: targetItem.values.followUp_operator,
          submitter_operator: targetItem.values.submitter_operator,
          avatar,
        };
      }
    },

    async submitCallReason(showAlert: boolean = false) {
      if (!this.callReason.comment || !this.callReason.followDate) return
      const body = {
        id: this.callerInfo.x_cid,
        comment: this.callReason.comment,
        nextFollowDate: this.callReason.followDate,
      };
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>(`/api/contact/call/reason`, {
          method: "POST",
          body,
        });
        if (!showAlert) return
        toastStore.showAlert({
          title: "Call Reason Insert Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
      } catch (err: any) {
        const errorText = err.response._data.data.error === "this operator has no duration" ? "this call not for you" : "Can Not Connect To Server"
        toastStore.showAlert({
          title: errorText,
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
      }
    },

    async submitReminder(params: any) {
      if (!this.callReason.followDate && !this.callReason.reminderType) return
      const toastStore = useToastStore();

      const follow_date_utc = isoDate(this.callReason.followDate)
      const follow_date = convertDateToUnix(follow_date_utc)

      let timestamp_offset = 0
      let reminder_date = 0
      if (this.callReason.reminderType) {
        switch (this.callReason.reminderType) {
          case 2:
            timestamp_offset = timeToSeconds('30m')
            break;
          case 3:
            timestamp_offset = timeToSeconds('1h')
            break;
          case 4:
            timestamp_offset = timeToSeconds('1d')
            break;
          case 5:
            const reminder_date_utc = isoDate(this.callReason.reminderDate)
            reminder_date = convertDateToUnix(reminder_date_utc)
            break;
        }
      }
      const body = {
        client_name: params.name,
        client_phone: params.phone_number,
        custom_reminder_timestamp: +reminder_date,
        follow_up_operator: params.followUp_operator,
        submit_operator: params.submitter_operator,
        timestamp: +follow_date,
        timestamp_offset,
        call_reason: this.callReason.comment
      }
      try {
        const data = await $fetch<any>(`/api/contact/call/reminder`, {
          method: "POST",
          body,
        });
      } catch (err: any) {
        toastStore.showAlert({
          title: "Can Not Connect To Server",
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
      }
    },
    resetReasonInfo() {
      this.callReason = {
        comment: "",
        followDate: "",
        reminderType: 0,
        reminderDate: ""
      }
    },

    async getCallCounts(type: string) {
      if (!this.user_phone) return;
      const cdrStore = useCdrStore();
      this.callsCount[type] = await cdrStore.getTotalPages(
        {
          call_type: type == "all" ? "" : type == "incoming" ? "in" : "out",
          destination: this.user_phone,
        },
        true
      );
    },

    async getCalls(params: any) {
      if (!this.user_phone) return;
      const cdrStore = useCdrStore();
      const data = await cdrStore.getCdrs(
        {
          call_type: params.type == "all" ? "" : params.type == "incoming" ? "in" : "out",
          destination: this.user_phone,
          offset: params.offset
        },
        true
      )
      data.forEach(element => {
        const shamsiDate = getShamsiDate(element.dateTime, true, false);
        const [date, time] = shamsiDate.split(" ");
        let followDate = "-"
        if (element.followDate) {
          const [follow_date, follow_time] = element.followDate.split(" ");
          const d = getShamsiDate(follow_date)
          followDate = `${follow_time ?? ""} ${d}`
        }
        this.calls[params.type].push({
          ...element,
          date,
          time,
          duration: convertSec(element.duration, true),
          type: params.type === "all" ? element.en_call_type : params.type,
          followDate: followDate
        });
      });
    },

    resetData() {
      this.calls.all = [];
      this.calls.outgoing = [];
      this.calls.incoming = [];
      this.calls.missed = [];
      this.callsCount.all = 0;
      this.callsCount.outgoing = 0;
      this.callsCount.incoming = 0;
      this.callsCount.missed = 0;
    },

    setLastPlayed(id, audioElement) {
      this.lastPlayedId = id;
      this.lastPlayedAudio = audioElement;
    },

    resetLastPlayed() {
      this.lastPlayedId = null;
      this.lastPlayedAudio = null;
    },
  },
});
